/**
 * Align Buildings/Energy queries on elec_*_format / ng_*_format with how rows are keyed in the DB:
 * - geography_type is lowercase in energy CSVs: city | county | state (and sometimes place for LODES-linked rows).
 * - geofips may be 7-digit zero-padded strings in format tables while population.geofips is often numeric
 *   (e.g. 6113 vs "06113" / "006113").
 */

/** Map population.geography_type (Title Case) to energy format table values to try, in order. */
export function energyFormatGeographyTypes(popGeographyType: string | null | undefined): string[] {
  const t = String(popGeographyType || '')
    .trim()
    .toLowerCase();
  // Pipelines / legacy rows use lowercase; some sources use Title Case — try both to avoid empty results.
  if (t === 'city') return ['city', 'place', 'City', 'Place'];
  if (t === 'county') return ['county', 'County'];
  if (t === 'state') return ['state', 'State'];
  return ['city', 'place', 'City', 'Place'];
}

/**
 * Candidate geofips strings to match format-table rows for a population geofips value.
 */
export function geofipsVariantsForEnergyFormat(geofips: string | number | null | undefined): string[] {
  if (geofips == null || geofips === '') return [];
  const raw = String(geofips)
    .replace(/\.0+$/, '')
    .trim();
  if (!raw) return [];

  const digits = raw.replace(/\D/g, '');
  if (!digits) return [raw];

  const stripped = digits.replace(/^0+/, '') || '0';
  const n = parseInt(stripped, 10);
  const out = new Set<string>();

  const add = (v: string) => {
    if (v && v.length > 0) out.add(v);
  };

  add(raw);
  add(digits);
  add(stripped);
  add(digits.padStart(7, '0'));
  add(stripped.padStart(7, '0'));
  add(stripped.padStart(5, '0'));

  // CA county: population often stores 6113 for FIPS 06113
  if (Number.isFinite(n) && stripped.length >= 3 && stripped.length <= 4 && stripped.startsWith('6')) {
    add(`0${stripped}`);
  }
  if (Number.isFinite(n) && n >= 60000 && n < 70000) {
    add(String(n).padStart(5, '0'));
    add(String(n).padStart(7, '0'));
  }

  return Array.from(out);
}

/** State row geofips variants in energy tables (California). */
export function stateGeofipsVariantsForEnergyFormat(): string[] {
  return ['6', '06', '0000006'];
}
